import React, { useReducer, useEffect, useMemo } from 'react';
import { Container, Box, Typography, TextField, Button, CircularProgress, Alert, Grid, Divider, Chip, Paper } from '@mui/material';
import { GitHub as GitHubIcon, Person as PersonIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
// import { getProjectById, evaluateProject } from '../../services/api';
import AppBreadcrumbs from '../../components/AppBreadcrumbs';
import useProjectStore from '../../store/useProjectStore';
import { useDashboardShell } from '../../context/DashboardShellContext';

const initialState = {
  project: null,
  loading: true,
  submitting: false,
  error: '',
  success: '',
  evaluation: {
    codeQuality: '',
    projectStructure: '',
    functionality: '',
    documentation: '',
    remarks: ''
  }
};

const evaluationReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        project: action.payload.project,
        evaluation: action.payload.evaluation || state.evaluation
      };
    case 'FETCH_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_EVALUATION':
      return {
        ...state,
        evaluation: { ...state.evaluation, [action.field]: action.value }
      };
    case 'SUBMIT_START':
      return { ...state, submitting: true, error: '', success: '' };
    case 'SUBMIT_SUCCESS':
      return { ...state, submitting: false, success: action.payload };
    case 'SUBMIT_FAILURE':
      return { ...state, submitting: false, error: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
};

const ProjectEvaluation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const shell = useDashboardShell();
  const workspace = shell?.workspacePath ?? '/faculty/dashboard';
  const projectBase = shell?.projectBasePath ?? '/faculty/project';
  const {
    projectById,
    loadingProjectById,
    errorProjectById,
    fetchProjectById,
    evaluateProject,
    evaluatingProject,
  } = useProjectStore();
  
  const [state, dispatch] = useReducer(evaluationReducer, initialState);
  const { project, loading, submitting, error, success, evaluation } = state;

  useEffect(() => {
    const load = async () => {
      dispatch({ type: 'FETCH_START' });
      try {
        const projectData = await fetchProjectById(id);
        const evaluationData = projectData?.evaluation ? {
          codeQuality: projectData.evaluation.codeQuality || '',
          projectStructure: projectData.evaluation.projectStructure || '',
          functionality: projectData.evaluation.functionality || '',
          documentation: projectData.evaluation.documentation || '',
          remarks: projectData.evaluation.remarks || ''
        } : null;
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: { project: projectData, evaluation: evaluationData }
        });
      } catch (err) {
        console.error('Error fetching project:', err);
        dispatch({ type: 'FETCH_FAILURE', payload: 'Failed to load project details.' });
      }
    };
    if (id && !projectById[id]) load();
    else if (projectById[id]) {
      const projectData = projectById[id];
      const evaluationData = projectData?.evaluation ? {
        codeQuality: projectData.evaluation.codeQuality || '',
        projectStructure: projectData.evaluation.projectStructure || '',
        functionality: projectData.evaluation.functionality || '',
        documentation: projectData.evaluation.documentation || '',
        remarks: projectData.evaluation.remarks || ''
      } : null;
      dispatch({ type: 'FETCH_SUCCESS', payload: { project: projectData, evaluation: evaluationData } });
    }
  }, [id, fetchProjectById, projectById]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: 'UPDATE_EVALUATION', field: name, value });
  };

  const calculatedScore = useMemo(() => {
    const { codeQuality, projectStructure, functionality, documentation } = evaluation;
    const total = Number(codeQuality || 0) + Number(projectStructure || 0) + Number(functionality || 0) + Number(documentation || 0);
    return Math.round((total / 40) * 100);
  }, [evaluation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch({ type: 'SUBMIT_START' });

    if (!evaluation.codeQuality || !evaluation.projectStructure || !evaluation.functionality || !evaluation.documentation) {
      dispatch({ type: 'SET_ERROR', payload: 'Please fill in all numeric scores (0-10).' });
      return; 
    }

    try {
      await evaluateProject(id, evaluation);
      dispatch({ type: 'SUBMIT_SUCCESS', payload: 'Evaluation submitted successfully!' });
    } catch (err) {
      dispatch({ type: 'SUBMIT_FAILURE', payload: err.response?.data?.message || 'Failed to submit evaluation.' });
    }
  };

  if (loading || loadingProjectById[id]) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{errorProjectById[id]?.message || 'Project not found.'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(workspace)} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(workspace)} sx={{ mb: 1 }}>
          Back to Dashboard
        </Button>

        <AppBreadcrumbs
          items={[
            { label: 'Dashboard', path: workspace },
            { label: project.title || 'Project', path: `${projectBase}/${id}` },
            { label: 'Evaluation' },
          ]}
        />

        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {project.title}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip 
              icon={<PersonIcon />} 
              label={project.student?.name || 'Unknown Student'} 
              variant="outlined" 
            />
            {project.student?.branch && <Chip label={project.student.branch} size="small" />}
            {project.student?.yearOfStudy && <Chip label={`Year ${project.student.yearOfStudy}`} size="small" />}
          </Box>

          <Typography variant="body1" paragraph>
            {project.description}
          </Typography>

          {project.githubRepoUrl && (
            <Button 
              variant="outlined" 
              startIcon={<GitHubIcon />} 
              href={project.githubRepoUrl} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              View Repository
            </Button>
          )}
        </Paper>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">
              Project Evaluation
            </Typography>
            <Chip 
              label={`Score: ${calculatedScore}%`} 
              color={calculatedScore >= 70 ? 'success' : calculatedScore >= 40 ? 'warning' : 'error'} 
              variant="filled"
              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
            />
          </Box>
          <Divider sx={{ mb: 3 }} />

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code Quality (0-10)"
                  name="codeQuality"
                  type="number"
                  inputProps={{ min: 0, max: 10 }}
                  value={evaluation.codeQuality}
                  onChange={handleChange}
                  required
                  disabled={submitting || success || evaluatingProject[id]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Project Structure (0-10)"
                  name="projectStructure"
                  type="number"
                  inputProps={{ min: 0, max: 10 }}
                  value={evaluation.projectStructure}
                  onChange={handleChange}
                  required
                  disabled={submitting || success || evaluatingProject[id]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Functionality (0-10)"
                  name="functionality"
                  type="number"
                  inputProps={{ min: 0, max: 10 }}
                  value={evaluation.functionality}
                  onChange={handleChange}
                  required
                  disabled={submitting || success || evaluatingProject[id]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Documentation (0-10)"
                  name="documentation"
                  type="number"
                  inputProps={{ min: 0, max: 10 }}
                  value={evaluation.documentation}
                  onChange={handleChange}
                  required
                  disabled={submitting || success || evaluatingProject[id]}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Overall Remarks"
                  name="remarks"
                  multiline
                  rows={4}
                  value={evaluation.remarks}
                  onChange={handleChange}
                  placeholder="Provide constructive feedback..."
                  disabled={submitting || success || evaluatingProject[id]}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting || success || evaluatingProject[id]}
              >
                {(submitting || evaluatingProject[id]) ? 'Submitting...' : 'Submit Evaluation'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ProjectEvaluation;
